// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import type { SkillFormData } from '../types/schemas';

const SKILLS_QUERY_KEY = ['skills'] as const;
const CREDITS_QUERY_KEY = ['credits'] as const;

/** Hook for fetching all skills with optional category filter. */
export function useSkills(filters?: { category?: string }) {
  return useQuery({
    queryKey: [...SKILLS_QUERY_KEY, filters],
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/api/v1/skills', {
        params: {
          query: {
            category: filters?.category || undefined,
          },
        },
      });

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
  });
}

/** Hook for creating a new skill. */
export function useCreateSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: SkillFormData) => {
      const yearsExperience = params.years_experience
        ? parseInt(params.years_experience, 10)
        : undefined;

      const { data, error } = await apiClient.POST('/api/v1/skills', {
        body: {
          name: params.name,
          category: params.category,
          proficiency: params.proficiency || undefined,
          years_experience:
            yearsExperience !== undefined && !isNaN(yearsExperience)
              ? yearsExperience
              : undefined,
        },
      });

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SKILLS_QUERY_KEY });
    },
  });
}

/** Hook for updating an existing skill. */
export function useUpdateSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...params
    }: SkillFormData & { id: string }) => {
      const yearsExperience = params.years_experience
        ? parseInt(params.years_experience, 10)
        : undefined;

      const { data, error } = await apiClient.PUT('/api/v1/skills/{id}', {
        params: { path: { id } },
        body: {
          name: params.name,
          category: params.category,
          proficiency: params.proficiency || undefined,
          years_experience:
            yearsExperience !== undefined && !isNaN(yearsExperience)
              ? yearsExperience
              : undefined,
        },
      });

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SKILLS_QUERY_KEY });
    },
  });
}

/** Hook for deleting a skill. */
export function useDeleteSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await apiClient.DELETE('/api/v1/skills/{id}', {
        params: { path: { id } },
      });

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SKILLS_QUERY_KEY });
    },
  });
}

type SuggestedSkill = {
  name: string;
  category: string;
  proficiency?: string;
};

type SuggestSkillsParams = {
  job_title?: string;
  include_experiences: boolean;
  include_accomplishments: boolean;
};

/** Hook for generating AI skill suggestions. */
export function useSuggestSkills() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: SuggestSkillsParams) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/skills/suggest',
        {
          body: {
            job_title: params.job_title || undefined,
            include_experiences: params.include_experiences,
            include_accomplishments: params.include_accomplishments,
          },
        },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data as SuggestedSkill[];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CREDITS_QUERY_KEY });
    },
  });
}

/** Hook for bulk deleting skills. */
export function useBulkDeleteSkills() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/skills/bulk-delete',
        { body: { ids } },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SKILLS_QUERY_KEY });
    },
  });
}
